import { z } from "zod";

const finite = () => z.number().finite().min(-1e9).max(1e9);
export const calculationLineSchema = z.object({
  id: z.string().min(1).max(100),
  code: z.string().max(100),
  name: z.string().min(1).max(1000),
  formula: z.string().max(1000),
  unit: z.string().max(30),
  quantity: finite(),
  laborRate: finite(),
  otherRate: finite(),
  originalQuantity: finite(),
  originalLabor: finite(),
  originalOther: finite(),
  printedRate: finite(),
});
export const calculationPositionSchema = z.object({
  code: z.string().min(1).max(60),
  name: z.string().min(1).max(1000),
  unit: z.string().min(1).max(30),
  section: z.enum(["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"]),
  lvPrice: finite().nullable(),
  k7Price: finite().nullable(),
  manualCalculation: z.boolean().optional(),
  originalLaborCost: finite(),
  originalOtherCost: finite(),
  originalLineLabor: finite().optional(),
  originalLineOther: finite().optional(),
  laborMarkup: finite(),
  otherMarkup: finite(),
  finalHourlyRate: z.number().finite().min(0).max(1e9).optional(),
  /** Explicit selling price, independent of the retained cost calculation. */
  priceOverride: finite().optional(),
  lines: z.array(calculationLineSchema).max(500),
  warnings: z.array(z.string().max(2000)).max(100),
  libraryPositionId: z.number().int().positive().optional(),
});
export const sourcePdfSchema = z.object({
  fileName: z.string().min(1).max(255),
  data: z.string().min(1).max(12 * 1024 * 1024).regex(/^[A-Za-z0-9+/]+={0,2}$/),
});
export const calculationImportSchema = z.object({
  name: z.string().trim().min(1).max(150),
  lv: sourcePdfSchema,
  k7: sourcePdfSchema,
});
export const calculationDraftSchema = z.object({
  name: z.string().trim().min(1).max(150),
  positions: z.array(calculationPositionSchema).min(1).max(2000),
  warnings: z.array(z.string().max(2000)).max(2000),
  sectionTitles: z.record(z.string().trim().min(1).max(150))
    .refine((titles) => Object.keys(titles).every((key) => /^s[1-8]$/.test(key)), "Ungültige Leistungsgruppe").optional(),
}).superRefine((value, context) => {
  const codes = new Set<string>();
  for (const position of value.positions) {
    if (codes.has(position.code)) context.addIssue({ code: "custom", message: `Doppelte Positionsnummer ${position.code}` });
    codes.add(position.code);
  }
});
export type CalculationLine = z.infer<typeof calculationLineSchema>;
export type CalculationPosition = z.infer<typeof calculationPositionSchema>;
export type CalculationDraft = z.infer<typeof calculationDraftSchema>;
export type CalculationSources = Pick<z.infer<typeof calculationImportSchema>, "lv" | "k7">;
export type CalculationTemplate = CalculationDraft & {
  origin?: "offer";
  id: string;
  revision: number;
  updatedAt: string;
  sourceFiles: { lv: string; k7: string };
};
export type CalculationTemplateSummary = Pick<CalculationTemplate, "id" | "name" | "revision" | "updatedAt"> & {
  positionCount: number;
  discrepancyCount: number;
};

export const roundMoney = (value: number) => Math.round((value + Math.sign(value) * Number.EPSILON) * 100) / 100;

/** Hourly material/equipment costs are not working hours. Zero-cost labor can
 * still receive the final rate, as can signed time deductions. */
export function isHourlyLaborLine(line: CalculationLine) {
  return /^(h|std)$/i.test(line.unit.trim()) &&
    (line.laborRate !== 0 || line.originalLabor !== 0 ||
      (line.otherRate === 0 && line.originalOther === 0));
}

/** Keep original rates and markups so removing the override restores K7. */
export function applyFinalHourlyRate(draft: CalculationDraft, rate: number | null): CalculationDraft {
  if (rate !== null && (!Number.isFinite(rate) || rate < 0 || rate > 1e9)) {
    throw new Error("Bitte einen gültigen Verrechnungssatz zwischen 0 und 1.000.000.000 €/h eingeben.");
  }
  return {
    ...draft,
    positions: draft.positions.map((position) => {
      const { finalHourlyRate: previousRate, ...original } = position;
      return rate === null ? original : { ...original, finalHourlyRate: rate };
    }),
  };
}

/** Printed manufacturing totals anchor the rounding in the original K7 document.
 * Changes use the effective row rates (not the often rounded Kosten/EH column). */
export function calculatePosition(position: CalculationPosition) {
  const finalRate = position.finalHourlyRate;
  const usesFinalRate = finalRate !== undefined && position.lines.some(isHourlyLaborLine);
  // Under an explicit selling-rate override, do not carry the original K7
  // labor rounding residual into hours × rate or apply the old labor markup.
  let laborCost = usesFinalRate ? 0 : position.originalLaborCost - (position.originalLineLabor ??
    position.lines.reduce((sum, line) => sum + line.originalLabor, 0));
  let otherCost = position.originalOtherCost - (position.originalLineOther ??
    position.lines.reduce((sum, line) => sum + line.originalOther, 0));
  for (const line of position.lines) {
    laborCost += line.quantity * (finalRate !== undefined && isHourlyLaborLine(line) ? finalRate : line.laborRate);
    otherCost += line.quantity * line.otherRate;
  }
  const labor = roundMoney(laborCost * (1 + (usesFinalRate ? 0 : position.laborMarkup) / 100));
  const other = roundMoney(otherCost * (1 + position.otherMarkup / 100));
  const hours = position.lines.filter(isHourlyLaborLine)
    .reduce((sum, line) => sum + line.quantity, 0);
  // LV-only rows stay visible, but never pretend to have a K7 calculation.
  const calculatedPrice = position.k7Price === null && !position.manualCalculation ? position.lvPrice ?? 0 : roundMoney(labor + other);
  const price = position.priceOverride ?? calculatedPrice;
  return { laborCost, otherCost, labor, other, price, hours };
}

export function hasSourceDiscrepancy(position: CalculationPosition) {
  return position.lvPrice !== null && position.k7Price !== null &&
    Math.abs(position.lvPrice - position.k7Price) >= 0.009;
}
